import React, { useState } from 'react';
import { FiCheckCircle, FiClock, FiDownload, FiGrid, FiImage, FiMail, FiX } from 'react-icons/fi';
import type { PurchasedTicketItem } from '../../api/tickets';
import { formatDate, formatTime } from '../../utils/eventDate';
import { triggerFileDownload } from '../../utils/download';

const EVENT_IMAGE_FALLBACK_SRC = '/Musicade-Logo.png';

interface TicketCardProps {
  ticket: PurchasedTicketItem;
}

export const TicketCard: React.FC<TicketCardProps> = ({ ticket }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const showImage = !!ticket.imageUrl && !imageFailed;

  return (
    <>
      <div className="flex flex-col overflow-hidden rounded-3xl border border-[#22242a] bg-[#181a20] shadow-sm sm:flex-row">
        {/* Image + status pills */}
        <div className="relative h-44 shrink-0 sm:h-auto sm:w-56">
          {showImage ? (
            <img
              src={ticket.imageUrl!}
              alt={ticket.label}
              className="h-full w-full object-cover"
              onError={(e) => {
                if (e.currentTarget.src.endsWith(EVENT_IMAGE_FALLBACK_SRC)) {
                  setImageFailed(true);
                  return;
                }
                e.currentTarget.src = EVENT_IMAGE_FALLBACK_SRC;
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-zinc-700">
              <FiImage size={26} />
            </div>
          )}
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-gradient-to-r from-fuchsia-600 to-violet-600 px-3 py-1 text-[11px] font-bold text-white shadow">
              {ticket.label}
            </span>
            {ticket.checkedIn && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold text-white shadow">
                <FiCheckCircle size={11} /> Checked in
              </span>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1 space-y-3 p-5">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-white">{ticket.label}</h3>
            {ticket.recipientEmail && (
              <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-zinc-400">
                <FiMail size={12} className="shrink-0" /> {ticket.recipientEmail}
              </p>
            )}
          </div>

          {ticket.checkedIn && ticket.checkInTime && (
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-800/80 text-zinc-300">
                <FiClock size={15} />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">
                  Checked in
                </p>
                <p className="text-sm font-semibold text-zinc-200">
                  {formatDate(ticket.checkInTime)} · {formatTime(ticket.checkInTime)}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-[#22242a] pt-3 text-[11px] text-zinc-500">
            <span>
              Pass Type: <span className="font-semibold text-white">{ticket.label}</span>
            </span>
            <span>
              Ticket: <span className="font-semibold text-white">#{ticket.ticketIndex + 1}</span>
            </span>
            <span className="font-mono">
              Ref: <span className="text-zinc-300">{ticket.uniqueId}</span>
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-2 border-t border-[#22242a] p-5 sm:w-56 sm:flex-col sm:border-l sm:border-t-0">
          <button
            type="button"
            onClick={() => ticket.qrCodeUrl && setShowQr(true)}
            disabled={!ticket.qrCodeUrl}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 px-4 py-2.5 text-xs font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none sm:w-full cursor-pointer"
          >
            <FiGrid size={14} /> Show QR Pass
          </button>

          <button
            type="button"
            onClick={() =>
              ticket.qrCodeUrl &&
              void triggerFileDownload(ticket.qrCodeUrl, `ticket-${ticket.uniqueId}.png`)
            }
            disabled={!ticket.qrCodeUrl}
            className="flex flex-1 items-center justify-center gap-2 rounded-full border border-zinc-700 bg-zinc-800/80 px-4 py-2.5 text-xs font-bold text-zinc-200 transition hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none sm:w-full cursor-pointer"
          >
            <FiDownload size={14} /> Download QrCode
          </button>
        </div>
      </div>

      {showQr && ticket.qrCodeUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          onClick={() => setShowQr(false)}
        >
          <div
            className="w-full max-w-xs space-y-4 rounded-3xl border border-[#2c2d33] bg-[#141518] p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="truncate text-sm font-bold text-white">{ticket.label}</h3>
              <button
                type="button"
                onClick={() => setShowQr(false)}
                className="text-zinc-400 transition hover:text-white cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>
            <img
              src={ticket.qrCodeUrl}
              alt="Ticket QR code"
              className="mx-auto h-56 w-56 rounded-2xl bg-white object-contain p-3"
            />
            <p className="font-mono text-[11px] text-zinc-500">{ticket.uniqueId}</p>
          </div>
        </div>
      )}
    </>
  );
};
